import React, { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  Home, Mail, Calendar, CheckSquare, BarChart3,
  Sun, Moon, Menu, Bell, Phone, ScrollText, Settings, X, UserRound, LogOut, ListChecks, Users,
} from "lucide-react";
import { useTheme } from "@/lib/theme";
import { VoiceSheet } from "@/components/VoiceSheet";
import { cn } from "@/lib/utils";
import { AcaiLogo } from "@/components/AcaiLogo";
import { getAuthStatus, logout } from "@/lib/apiClient";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { ReminderNotifier } from "@/components/ReminderNotifier";
import { NotificationBell } from "@/components/NotificationBell";
import {useWorkspaceEvents} from '@/lib/useWorkspaceEvents';

const MAIN_NAV = [
  { to: "/", label: "Koti", icon: Home, testid: "nav-home", end: true },
  { to: "/inbox", label: "Posti", icon: Mail, testid: "nav-inbox" },
  { action: "voice", label: "Assistentti", testid: "voice-assistant-button" },
  { to: "/calendar", label: "Kalenteri", icon: Calendar, testid: "nav-calendar" },
  { to: "/tasks", label: "Tehtävät", icon: CheckSquare, testid: "nav-tasks" },
];

const MORE_NAV = [
  { to: "/collaboration", label: "Yhteistyö", icon: Users, testid: "nav-collaboration" },
  { to: "/approvals", label: "Hyväksynnät", icon: ListChecks, testid: "nav-approvals" },
  { to: "/growth", label: "Kasvujohtaja", icon: BarChart3, testid: "nav-growth" },
  { to: "/reminders", label: "Muistutukset", icon: Bell, testid: "nav-reminders" },
  { to: "/contacts", label: "Yhteystiedot & luvat", icon: Phone, testid: "nav-contacts" },
  { to: "/audit", label: "Tapahtumaloki", icon: ScrollText, testid: "nav-audit" },
  { to: "/settings", label: "Asetukset ja yhteydet", icon: Settings, testid: "nav-settings" },
];

export const Layout = ({ children }) => {
  const { theme, toggle } = useTheme();
  const [voiceOpen, setVoiceOpen] = useState(false);
  const [voiceInitialPrompt, setVoiceInitialPrompt] = useState("");
  const [collaborationRequestId, setCollaborationRequestId] = useState(null);
  const [emailTaskSuggestionId,setEmailTaskSuggestionId]=useState(null);
  const [workContext,setWorkContext]=useState(null);
  const [autoStartVoice,setAutoStartVoice]=useState(false);
  const [initialSolution,setInitialSolution]=useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [account, setAccount] = useState(null);
  useEffect(()=>{let active=true;(async()=>{try{const value=await getAuthStatus();if(active)setAccount(value.user||null);}catch{if(active)setAccount(null);}})();return()=>{active=false;};},[]);
  const [liveState,setLiveState]=useState('connected');
  useEffect(()=>{const update=event=>setLiveState(event.detail?.state);window.addEventListener('acai-live-status',update);return()=>window.removeEventListener('acai-live-status',update);},[]);
  useWorkspaceEvents(Boolean(account?.id));
  const location = useLocation();
  const menuTriggerRef = React.useRef(null);

  useEffect(() => {
    const openRequest = event => {
      const requestId = event.detail?.requestId;
      if (typeof requestId !== "string" || !requestId || requestId.length > 100) return;
      setCollaborationRequestId(requestId);
      setInitialSolution(null);
      setAutoStartVoice(event.detail?.startVoice===true);
      setWorkContext(null);
      setEmailTaskSuggestionId(null);
      setVoiceInitialPrompt(event.detail?.autoSummarize === false ? "" : "Tiivistä valittu yhteistyöpyyntö. Älä vielä ehdota vastausta.");
      setVoiceOpen(true);
    };
    window.addEventListener("acai-collaboration-assistant", openRequest);
    return () => window.removeEventListener("acai-collaboration-assistant", openRequest);
  }, []);

  useEffect(()=>{
    const openTask=event=>{
      const id=event.detail?.suggestionId;
      if(typeof id!=="string"||!/^[a-f0-9]{64}$/.test(id))return;
      setAutoStartVoice(event.detail?.startVoice===true);
      setEmailTaskSuggestionId(id);setWorkContext(null);setCollaborationRequestId(null);setVoiceInitialPrompt("");setVoiceOpen(true);
      setInitialSolution(null);
    };
    window.addEventListener("acai-email-task-assistant",openTask);
    return()=>window.removeEventListener("acai-email-task-assistant",openTask);
  },[]);

  useEffect(()=>{
    const openWork=event=>{
      const reference=event.detail;
      if(!["task","calendar_event","email","collaboration_request"].includes(reference?.kind)||typeof reference.target_id!=="string"||!reference.target_id.trim()||reference.target_id.length>300)return;
      setAutoStartVoice(reference.startVoice===true);
      setInitialSolution(reference.solution&&typeof reference.solution.response==="string"&&reference.solution.response.length<=3000?reference.solution:null);
      setWorkContext({kind:reference.kind,target_id:reference.target_id});
      setEmailTaskSuggestionId(null);setCollaborationRequestId(null);setVoiceInitialPrompt("");setVoiceOpen(true);
    };
    window.addEventListener("acai-work-context-assistant",openWork);
    return()=>window.removeEventListener("acai-work-context-assistant",openWork);
  },[]);

  useEffect(() => {
    const parameters = new URLSearchParams(location.search);
    if (parameters.get("morning") !== "1") return;
    setEmailTaskSuggestionId(null);setCollaborationRequestId(null);
    setWorkContext(null);
    setAutoStartVoice(false);
    setInitialSolution(null);
    setVoiceInitialPrompt("Anna aamuyhteenveto sähköposteista, kalenterista, tehtävistä, muistutuksista ja minulle merkittävistä uutisista");
    setVoiceOpen(true);
    parameters.delete("morning");
    const nextQuery = parameters.toString();
    window.history.replaceState({}, "", `${location.pathname}${nextQuery ? `?${nextQuery}` : ""}${location.hash}`);
  }, [location.hash, location.pathname, location.search]);

  const toggleProfile = async () => {
    const nextOpen = !profileOpen;
    setProfileOpen(nextOpen);
    if (nextOpen && !account) {
      try {
        const status = await getAuthStatus();
        setAccount(status.user);
      } catch {
        setAccount(null);
      }
    }
  };

  const signOut = async () => {
    await logout();
    window.location.assign("/");
  };

  return (
    <div className="app-shell min-h-screen">
      <ReminderNotifier />
      <div className="ambient-orbit ambient-orbit-one" aria-hidden="true" />
      <div className="ambient-orbit ambient-orbit-two" aria-hidden="true" />
      <div className="ambient-star ambient-star-one" aria-hidden="true" />
      <div className="ambient-star ambient-star-two" aria-hidden="true" />
      <div className="app-viewport max-w-[480px] mx-auto min-h-screen relative pb-40">
        {/* Header */}
        <header className="app-header sticky top-0 z-30 px-5 pt-4 pb-2 grid grid-cols-[48px_1fr_96px] items-center">
          <button
            type="button"
            ref={menuTriggerRef}
            data-testid="more-menu-btn"
            onClick={() => setMenuOpen(true)}
            aria-label="Avaa valikko"
            className="header-round-button"
          >
            <Menu className="h-5 w-5" />
          </button>
          <AcaiLogo compact className="justify-self-center" />
          <div className="flex items-center justify-end">
          <NotificationBell />
          <button
            type="button"
            aria-label="Profiili"
            aria-expanded={profileOpen}
            onClick={toggleProfile}
            className="header-round-button relative justify-self-end"
          >
            <UserRound className="h-5 w-5" />
          </button>
          </div>
          {profileOpen && (
            <div className="absolute right-4 top-[68px] z-50 w-64 rounded-2xl border border-border bg-card p-4 text-left shadow-2xl fade-up">
              <p className="truncate text-sm font-semibold text-foreground">
                {account?.name || "Paikallinen käyttäjä"}
              </p>
              {account?.email && <p className="mt-1 truncate text-xs text-muted-foreground">{account.email}</p>}
              <p className="mt-2 text-[11px] text-muted-foreground">
                {account?.role === "owner" ? "Pääkäyttäjä" : account?.role === "partner" ? "Kumppani" : "Paikallinen esikatselu"}
              </p>
              <button
                type="button"
                onClick={signOut}
                className="mt-4 flex w-full items-center justify-center gap-2 rounded-xl border border-border px-3 py-2.5 text-sm text-foreground hover:bg-muted"
              >
                <LogOut className="h-4 w-4" /> Kirjaudu ulos
              </button>
            </div>
          )}
        </header>

        {/* Safety strip */}


        <main className="px-4 pt-4 pb-36 relative z-10">{liveState==='disconnected'&&<div role="alert" className="mb-4 rounded-xl border border-amber-500/40 p-3 text-sm">Automaattinen päivitysyhteys katkesi. Näkyvät tiedot voivat olla vanhoja. <button type="button" className="min-h-11 text-primary underline" onClick={()=>window.dispatchEvent(new Event('acai-live-retry'))}>Yhdistä uudelleen</button></div>}{children}</main>

        {/* Bottom nav */}
        <nav className="bottom-nav fixed bottom-3 left-1/2 -translate-x-1/2 z-30 w-[calc(100%-24px)] max-w-[456px] px-2 py-2 flex justify-around items-center">
          {MAIN_NAV.map(({ to, label, icon: Icon, testid, end, action }) => action === "voice" ? (
            <button
              key={action}
              type="button"
              data-testid={testid}
              onClick={() => setVoiceOpen(true)}
              className="nav-voice-item flex min-w-[56px] flex-col items-center gap-1 text-[10px] font-semibold text-foreground"
              aria-label="Avaa assistentti: puhu tai kirjoita" aria-haspopup="dialog"
            >
              <span className="nav-voice-orb"><img src="/assets/voice-orb-glass-v1.png" alt="" /></span>
              <span>{label}</span>
            </button>
          ) : (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={testid}
              className={({ isActive }) =>
                cn(
                  "nav-item flex flex-col items-center gap-1 px-1 py-1 rounded-xl text-[10px] font-medium transition-colors min-w-[52px] relative",
                  isActive ? "is-active text-foreground" : "text-muted-foreground hover:text-foreground"
                )
              }
            >
              <Icon className="h-5 w-5" />
              {label}
            </NavLink>
          ))}
        </nav>

        {/* More drawer */}
        <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
          <SheetContent side="left" hideClose data-testid="more-drawer" onCloseAutoFocus={(event) => { event.preventDefault(); menuTriggerRef.current?.focus(); }} className="menu-drawer w-80 max-w-[88%] overflow-y-auto">
            <SheetTitle className="sr-only">Valikko</SheetTitle><SheetDescription className="sr-only">Muut alueet ja teema</SheetDescription>
              <div className="flex items-center justify-between mb-6">
                <AcaiLogo compact />
                <button type="button" data-testid="more-drawer-close" onClick={() => setMenuOpen(false)} className="rounded-full p-1.5 hover:bg-muted" aria-label="Sulje valikko">
                  <X className="h-5 w-5 text-muted-foreground" />
                </button>
              </div>
              <div className="space-y-1">
                {MORE_NAV.map(({ to, label, icon: Icon, testid }) => (
                  <NavLink
                    key={to}
                    to={to}
                    data-testid={testid}
                    onClick={() => setMenuOpen(false)}
                    className={({ isActive }) =>
                      cn(
                        "flex items-center gap-3 rounded-xl px-3 py-3 text-sm font-medium transition-colors",
                        isActive ? "bg-primary/10 text-primary" : "text-foreground hover:bg-muted"
                      )
                    }
                  >
                    <Icon className="h-5 w-5" />
                    {label}
                  </NavLink>
                ))}
              </div>
              <button
                type="button"
                data-testid="theme-toggle-btn"
                onClick={toggle}
                className="mt-6 w-full flex items-center gap-3 rounded-xl border border-border px-3 py-3 text-sm text-muted-foreground hover:text-foreground"
              >
                {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                {theme === "dark" ? "Vaihda vaaleaan teemaan" : "Vaihda tummaan teemaan"}
              </button>
          </SheetContent>
        </Sheet>

        <VoiceSheet
          key={workContext ? `${workContext.kind}:${workContext.target_id}` : emailTaskSuggestionId || collaborationRequestId || "personal"}
          workContext={workContext}
          autoStartVoice={autoStartVoice}
          initialSolution={initialSolution}
          emailTaskSuggestionId={emailTaskSuggestionId}
          collaborationRequestId={collaborationRequestId}
          open={voiceOpen}
          onOpenChange={(nextOpen) => {
            setVoiceOpen(nextOpen);
            if (!nextOpen) { setVoiceInitialPrompt(""); setInitialSolution(null); setAutoStartVoice(false); setWorkContext(null); setCollaborationRequestId(null); setEmailTaskSuggestionId(null); }
          }}
          initialPrompt={voiceInitialPrompt}
        />
      </div>
    </div>
  );
};
